import {
  ColDef,
  GridOptions,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core';

import { AgTooltipComponent } from '@features/inline-budget/ag-tooltip.component';

import { rawAttributeColumnRenderer } from '@shared/components/ag-raw-attribute-renderer/ag-raw-attribute-renderer';
import { AgRawTooltip } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { TableConstants } from '@shared/constants/table.constants';
import {
  BudgetActivityAttributes,
  Currency,
  listBudgetActivitiesQuery,
} from '@shared/services/gql.service';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { Utils } from '@shared/utils/utils';

import { MapActivityType } from './map-activities-modal/map-activities-modal.model';
import { MapActivitiesTabGridRow } from './map-activities-tab.model';

export const getMapActivitiesTabGridOptions = (
  vendorCurrency: Currency,
  attributes: BudgetActivityAttributes[],
  budgetActivityOptions: listBudgetActivitiesQuery[],
  gridData: MapActivitiesTabGridRow[]
): GridOptions => {
  return <GridOptions>{
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    columnDefs: [
      {
        cellClass: ['!block', 'truncate', 'text-left'],
        comparator: (a, b) => {
          return Utils.localeAlphaNumSort(a || '', b || '');
        },
        field: 'invoice_item_description',
        headerName: 'Invoice Item',
        minWidth: 240,
        tooltipField: 'invoice_item_description',
      },
      {
        cellClass: ['!block', 'ag-cell-align-right', 'truncate'],
        field: 'amount',
        headerName: 'Total',
        minWidth: 240,
        tooltipValueGetter: (params: ITooltipParams) =>
          params.data?.amount ? Utils.agCurrencyFormatter(params.data?.amount, vendorCurrency) : '',
        valueFormatter: (params) => {
          return Utils.agCurrencyFormatter(params, vendorCurrency);
        },
      },
      {
        cellClass: ['!block', 'truncate', 'text-left'],
        field: 'activity_id',
        headerName: 'Budget Activity',
        minWidth: 240,
        tooltipField: 'activity_name',
        valueGetter: (params: ValueGetterParams) => {
          return params.data?.activity_name || '';
        },
      },
      {
        cellClass: ['!block', 'truncate', 'text-left'],
        field: 'activity_type',
        headerName: 'Cost Category',
        minWidth: 240,
        tooltipValueGetter: (params: ITooltipParams) =>
          params.data?.activity_type
            ? MapActivityType[params.data?.activity_type as keyof typeof MapActivityType]
            : '',
        valueFormatter: (params: ValueFormatterParams) => {
          return params.data?.activity_type
            ? MapActivityType[params.data?.activity_type as keyof typeof MapActivityType]
            : '';
        },
      },
      ...getMapActivitiesAttributeColumns(attributes, budgetActivityOptions, gridData),
    ],
    defaultColDef: {
      tooltipComponent: AgTooltipComponent,
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      editable: () => false,
      resizable: true,
    },
    stopEditingWhenCellsLoseFocus: false,
    tooltipShowDelay: 0,
  };
};

export const getMapActivitiesAttributeColumns = (
  attributes: BudgetActivityAttributes[],
  budgetActivityOptions: listBudgetActivitiesQuery[],
  gridData: MapActivitiesTabGridRow[]
): ColDef[] => {
  return (
    (attributes.map((attribute, index) => ({
      field: getEncodedAttributeName(attribute.attribute_name || ''),
      headerClass: ['ag-header-clamp'],
      headerName: attribute.attribute_name,
      headerTooltip: attribute.attribute_name,
      ...rawAttributeColumnRenderer({ gridData }, attribute.attribute_name || '', index),
      cellClass: ['!block', 'truncate', 'text-left', 'old-attribute-cell', '!leading-[26px]'],
      editable: false,
      minWidth: TableConstants.ATTRIBUTE_CELL_MIN_WIDTH,
      resizable: true,
      suppressFillHandle: true,
      suppressSizeToFit: true,
      tooltipComponent: AgRawTooltip,
      tooltipValueGetter: (params: ITooltipParams) => {
        if (params.data.activity_id && budgetActivityOptions.length > 0) {
          const optionAttributes =
            budgetActivityOptions.find((option) => option.id === params.data.activity_id)
              ?.attributes || [];

          return (
            optionAttributes.find((att) => att.attribute_name === attribute.attribute_name)
              ?.attribute_value || null
          );
        }

        return null;
      },
      valueGetter: (params: ValueGetterParams) => {
        if (params.data.activity_id && budgetActivityOptions.length > 0) {
          const optionAttributes =
            budgetActivityOptions.find((option) => option.id === params.data.activity_id)
              ?.attributes || [];

          return (
            optionAttributes.find((att) => att.attribute_name === attribute.attribute_name)
              ?.attribute_value || null
          );
        }

        return null;
      },
    })) as ColDef[]) || []
  );
};
